// v2/hooks/useOrders.js — /api/v2/orders/ (gruplanmis B2B siparisleri) + /summary/
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, qs } from '../utils/api';
import { useDebounced } from './useCatalog';
import { urunGorseli } from '../utils/mapProduct';

const sayi = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const dizi = (v) => (Array.isArray(v) ? v : []);

function mapLine(l) {
  return {
    id: l.id,
    orderNo: l.orderNo || '',
    erpNo: l.erpNo || '',
    code: l.code || '',
    name: l.name || '',
    group: l.group || '',
    grossPrice: sayi(l.grossPrice),
    netPrice: sayi(l.netPrice),
    discountRate: sayi(l.discountRate),
    discounts: dizi(l.discounts).map((d) => ({
      rate: sayi(d.rate), kind: d.kind === 'general' ? 'general' : 'customer',
    })).filter((d) => d.rate > 0),
    discountExact: Boolean(l.discountExact),
    qty: sayi(l.qty),
    qtyGzt: sayi(l.qtyGzt),
    qtyIst: sayi(l.qtyIst),
    total: sayi(l.total),
    status: l.status || 'processing',
    image: urunGorseli(l.code, 1),
  };
}

export function mapOrder(o) {
  return {
    id: String(o.id),
    no: o.no || '',
    date: o.date || '',
    time: o.time || '',
    orderNos: dizi(o.orderNos),
    erpNos: dizi(o.erpNos),
    lineCount: sayi(o.lineCount),
    qty: sayi(o.qty),
    total: sayi(o.total),
    grossTotal: sayi(o.grossTotal),
    warehouses: dizi(o.warehouses),
    status: o.status || 'processing',
    note: o.note || '',
    lines: dizi(o.lines).map(mapLine),
  };
}

function mapSummary(v) {
  const d = v || {};
  const y = d.year || {};
  const m = d.month || {};
  const t = d.totals || {};
  return {
    year: { year: sayi(y.year), orders: sayi(y.orders), amount: sayi(y.amount), average: sayi(y.average) },
    month: {
      orders: sayi(m.orders),
      amount: sayi(m.amount),
      previousToDate: sayi(m.previousToDate),
      compareDay: sayi(m.compareDay),
      changePct: m.changePct === null || m.changePct === undefined ? null : sayi(m.changePct),
    },
    totals: { orders: sayi(t.orders), amount: sayi(t.amount) },
    processing: sayi(d.processing),
    errors: sayi(d.errors),
    monthly: dizi(d.monthly).map((x) => ({
      id: x.id, label: x.label, year: sayi(x.year), orders: sayi(x.orders), amount: sayi(x.amount),
    })),
  };
}

const BOS = { orders: [], totalItems: 0, totalPages: 1, page: 1, totalAmount: 0, totalQty: 0 };

export function useOrders(f) {
  const q = useDebounced(f.q, 350);
  const ozel = f.period === 'custom';
  const anahtar = qs({
    q: q || undefined,
    period: f.period !== 'all' ? f.period : undefined,
    from: ozel && f.from ? f.from : undefined,
    to: ozel && f.to ? f.to : undefined,
    wh: f.wh || undefined,
    status: f.status || undefined,
    page: f.page,
    per_page: f.per,
  });

  const [sonuc, setSonuc] = useState(BOS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const ilk = useRef(true);
  const abortRef = useRef(null);

  const getir = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    if (ilk.current) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const veri = await apiGet(`/v2/orders/${anahtar}`, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      const d = veri || {};
      setSonuc({
        orders: dizi(d.results).map(mapOrder),
        totalItems: sayi(d.total_items),
        totalPages: Math.max(sayi(d.total_pages), 1),
        page: Math.max(sayi(d.page), 1),
        totalAmount: sayi(d.total_amount),
        totalQty: sayi(d.total_qty),
      });
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      // onceki liste ekranda kalir, sadece hata gosterilir
      setError(e.message || 'Siparişler alınamadı');
    } finally {
      if (ctrl.signal.aborted === false) {
        ilk.current = false;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [anahtar]);

  useEffect(() => {
    getir();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [getir]);

  return { ...sonuc, loading, refreshing, error, yenile: getir, aramaBekliyor: q !== f.q };
}

export function useOrderSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const yenile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const v = await apiGet('/v2/orders/summary/');
      setData(mapSummary(v));
    } catch (e) {
      setError(e.message || 'Özet alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { yenile(); }, [yenile]);

  return { data, loading, error, yenile };
}
